import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  Settings2, Shield, Clock, Image, Globe, Link, DollarSign,
  AlertCircle, Save, Book, Flag, Filter, Loader2,
} from 'lucide-react';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import {
  useListingSettingsQuery,
  useUpdateListingSettingsMutation,
} from '@/client/api/listing-management/listing-settings.query';

// The whole settings tree persists under one namespace; the PUT partial-merges
// its top-level keys, so sending the full object is safe.
const SETTINGS_NAMESPACE = 'listings';

interface ListingSettings {
  general: {
    listingDuration: number;
    maxActiveListings: number;
    maxImagesPerListing: number;
    allowedImageTypes: string[];
    maxImageSize: number;
    hideExpiredListings: boolean;
    allowListingRenewal: boolean;
  };
  affiliate: {
    allowAffiliateLinks: boolean;
    affiliateDisclosure: string;
    maxLinksPerListing: number;
    allowedDomains: string[];
    requireDisclosure: boolean;
    commissionRate: number;
  };
  display: {
    showViews: boolean;
    showBookmarks: boolean;
    showShareButtons: boolean;
    showContactInfo: boolean;
    defaultSortOrder: string;
    itemsPerPage: number;
  };
  pricing: {
    allowPriceRange: boolean;
    requirePrice: boolean;
    minPrice: number;
    maxPrice: number;
    defaultCurrency: string;
    supportedCurrencies: string[];
  };
  security: {
    requirePhoneVerification: boolean;
    requireEmailVerification: boolean;
    maxReportsBeforeReview: number;
    blockedKeywords: string[];
    spamProtection: boolean;
    contentScanning: boolean;
    ipTracking: boolean;
    maxListingsPerHour: number;
    maxListingsPerDay: number;
    enableSecurityLogs: boolean;
    logRetentionDays: number;
  };
}

const ListingSystemSettings = () => {
  useBreadcrumbs([
    { label: 'Listing Management', href: '#' },
    { label: 'Listing Settings' }
  ]);
  const [activeTab, setActiveTab] = useState('general');
  const [hasChanges, setHasChanges] = useState(false);
  
  const [settings, setSettings] = useState<ListingSettings>({
    general: {
      listingDuration: 30,
      maxActiveListings: 10,
      maxImagesPerListing: 8,
      allowedImageTypes: ['jpg', 'jpeg', 'png', 'webp'],
      maxImageSize: 5,
      hideExpiredListings: true,
      allowListingRenewal: true
    },
    affiliate: {
      allowAffiliateLinks: true,
      affiliateDisclosure: 'This listing contains affiliate links',
      maxLinksPerListing: 5,
      allowedDomains: ['booking.com', 'expedia.com', 'hotels.com'],
      requireDisclosure: true,
      commissionRate: 10
    },
    display: {
      showViews: true,
      showBookmarks: true,
      showShareButtons: true,
      showContactInfo: true,
      defaultSortOrder: 'newest',
      itemsPerPage: 24
    },
    pricing: {
      allowPriceRange: true,
      requirePrice: true,
      minPrice: 0,
      maxPrice: 999999,
      defaultCurrency: 'USD',
      supportedCurrencies: ['USD', 'EUR', 'GBP']
    },
    security: {
      requirePhoneVerification: false,
      requireEmailVerification: true,
      maxReportsBeforeReview: 3,
      blockedKeywords: [],
      spamProtection: true,
      contentScanning: true,
      ipTracking: true,
      maxListingsPerHour: 5,
      maxListingsPerDay: 20,
      enableSecurityLogs: true,
      logRetentionDays: 90
    }
  });

  const { data: persisted, isLoading } = useListingSettingsQuery(SETTINGS_NAMESPACE);
  const updateMutation = useUpdateListingSettingsMutation();

  // Hydrate once from the API — deep-merge persisted values over the defaults so
  // newer default keys still appear even if the stored blob predates them.
  const hydratedRef = React.useRef(false);
  useEffect(() => {
    const stored = persisted?.settings;
    if (!stored || hydratedRef.current) return;
    hydratedRef.current = true;
    setSettings(prev => {
      const next: any = { ...prev };
      (Object.keys(prev) as (keyof ListingSettings)[]).forEach(section => {
        if (stored[section] && typeof stored[section] === 'object') {
          next[section] = { ...prev[section], ...stored[section] };
        }
      });
      return next;
    });
  }, [persisted]);

  const handleSettingChange = (section: keyof ListingSettings, key: string, value: any) => {
    setSettings(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [key]: value
      }
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      await updateMutation.mutateAsync({ namespace: SETTINGS_NAMESPACE, settings });
      setHasChanges(false);
      toast.success('Settings saved');
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to save settings.');
    }
  };

  const navigationItems = [
    { id: 'general', label: 'General', icon: Settings2 },
    { id: 'affiliate', label: 'Affiliate', icon: Link },
    { id: 'display', label: 'Display', icon: Book },
    { id: 'pricing', label: 'Pricing', icon: DollarSign },
    { id: 'security', label: 'Security', icon: Shield }
  ];

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-8xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Listing Settings</h1>
              <p className="mt-1 text-sm text-gray-500">
                Configure system settings for listings management
              </p>
            </div>
            <button
              onClick={handleSave}
              disabled={!hasChanges || updateMutation.isPending || isLoading}
              className={`px-4 py-2 rounded-md flex items-center space-x-2 ${
                hasChanges && !updateMutation.isPending
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'
              }`}
            >
              {updateMutation.isPending ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <Save className="h-5 w-5" />
              )}
              <span>{updateMutation.isPending ? 'Saving…' : 'Save Changes'}</span>
            </button>
          </div>
        </div>

        <div className="flex space-x-6">
          {/* Navigation */}
          <div className="w-64 flex-shrink-0">
            <nav className="space-y-1">
              {navigationItems.map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  onClick={() => setActiveTab(id)}
                  className={`w-full flex items-center space-x-3 px-4 py-2 rounded-lg text-sm font-medium ${
                    activeTab === id
                      ? 'bg-blue-50 text-blue-700'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <Icon className="h-5 w-5" />
                  <span>{label}</span>
                </button>
              ))}
            </nav>
          </div>

          {/* Content Area */}
          <div className="flex-1 bg-white rounded-lg shadow">
            <div className="p-6">
              {/* General Settings */}
              {activeTab === 'general' && (
                <div className="space-y-6">
                  <h2 className="text-lg font-medium text-gray-900 mb-6">General Settings</h2>
                  
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Default Listing Duration (days)
                      </label>
                      <input
                        type="number"
                        value={settings.general.listingDuration}
                        onChange={(e) => handleSettingChange('general', 'listingDuration', parseInt(e.target.value))}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      />
                      <p className="mt-1 text-sm text-gray-500">
                        How long listings stay active by default
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Maximum Active Listings
                      </label>
                      <input
                        type="number"
                        value={settings.general.maxActiveListings}
                        onChange={(e) => handleSettingChange('general', 'maxActiveListings', parseInt(e.target.value))}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      />
                      <p className="mt-1 text-sm text-gray-500">
                        Maximum number of active listings per user
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Maximum Images per Listing
                      </label>
                      <input
                        type="number"
                        value={settings.general.maxImagesPerListing}
                        onChange={(e) => handleSettingChange('general', 'maxImagesPerListing', parseInt(e.target.value))}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Maximum Image Size (MB)
                      </label>
                      <input
                        type="number"
                        value={settings.general.maxImageSize}
                        onChange={(e) => handleSettingChange('general', 'maxImageSize', parseInt(e.target.value))}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  <div className="space-y-4 mt-8">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-sm font-medium text-gray-700">Hide Expired Listings</h3>
                        <p className="text-sm text-gray-500">
                          Automatically hide listings after they expire
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={settings.general.hideExpiredListings}
                          onChange={(e) => handleSettingChange('general', 'hideExpiredListings', e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-sm font-medium text-gray-700">Allow Listing Renewal</h3>
                        <p className="text-sm text-gray-500">
                          Allow users to renew their expired listings
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={settings.general.allowListingRenewal}
                          onChange={(e) => handleSettingChange('general', 'allowListingRenewal', e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                  </div>

                  <div className="mt-8">
                    <label className="block text-sm font-medium text-gray-700">
                      Allowed Image Types
                    </label>
                    <div className="mt-2 space-y-2">
                      {['jpg', 'jpeg', 'png', 'webp', 'gif'].map((type) => (
                        <label key={type} className="inline-flex items-center mr-6">
                          <input
                            type="checkbox"
                            checked={settings.general.allowedImageTypes.includes(type)}
                            onChange={(e) => {
                              const updatedTypes = e.target.checked
                                ? [...settings.general.allowedImageTypes, type]
                                : settings.general.allowedImageTypes.filter(t => t !== type);
                              handleSettingChange('general', 'allowedImageTypes', updatedTypes);
                            }}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="ml-2 text-sm text-gray-600">.{type}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Affiliate Settings */}
              {activeTab === 'affiliate' && (
                <div className="space-y-6">
                  <h2 className="text-lg font-medium text-gray-900 mb-6">Affiliate Settings</h2>
                  
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Maximum Links per Listing
                      </label>
                      <input
                        type="number"
                        value={settings.affiliate.maxLinksPerListing}
                        onChange={(e) => handleSettingChange('affiliate', 'maxLinksPerListing', parseInt(e.target.value))}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Commission Rate (%)
                      </label>
                      <input
                        type="number"
                        value={settings.affiliate.commissionRate}
                        onChange={(e) => handleSettingChange('affiliate', 'commissionRate', parseInt(e.target.value))}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Affiliate Disclosure Text
                    </label>
                    <textarea
                      value={settings.affiliate.affiliateDisclosure}
                      onChange={(e) => handleSettingChange('affiliate', 'affiliateDisclosure', e.target.value)}
                      rows={3}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                    </div>
                    <div className="space-y-4 mt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-sm font-medium text-gray-700">Allow Affiliate Links</h3>
                        <p className="text-sm text-gray-500">
                          Enable affiliate link functionality in listings
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={settings.affiliate.allowAffiliateLinks}
                          onChange={(e) => handleSettingChange('affiliate', 'allowAffiliateLinks', e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-sm font-medium text-gray-700">Require Disclosure</h3>
                        <p className="text-sm text-gray-500">
                          Require affiliate disclosure on listings with affiliate links
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={settings.affiliate.requireDisclosure}
                          onChange={(e) => handleSettingChange('affiliate', 'requireDisclosure', e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                  </div>

                  <div className="mt-6">
                    <label className="block text-sm font-medium text-gray-700">
                      Allowed Affiliate Domains
                    </label>
                    <div className="mt-2">
                      <input
                        type="text"
                        placeholder="Add domain and press Enter"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const input = e.target as HTMLInputElement;
                            const domain = input.value.trim();
                            if (domain && !settings.affiliate.allowedDomains.includes(domain)) {
                              handleSettingChange('affiliate', 'allowedDomains', [
                                ...settings.affiliate.allowedDomains,
                                domain
                              ]);
                              input.value = '';
                            }
                          }
                        }}
                      />
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {settings.affiliate.allowedDomains.map((domain) => (
                        <span
                          key={domain}
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                        >
                          {domain}
                          <button
                            type="button"
                            onClick={() => {
                              handleSettingChange(
                                'affiliate',
                                'allowedDomains',
                                settings.affiliate.allowedDomains.filter(d => d !== domain)
                              );
                            }}
                            className="ml-1 inline-flex items-center p-0.5 rounded-full text-blue-400 hover:bg-blue-200 hover:text-blue-500"
                          >
                            <span className="sr-only">Remove</span>
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Display Settings */}
              {activeTab === 'display' && (
                <div className="space-y-6">
                  <h2 className="text-lg font-medium text-gray-900 mb-6">Display Settings</h2>
                  
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Items Per Page
                      </label>
                      <select
                        value={settings.display.itemsPerPage}
                        onChange={(e) => handleSettingChange('display', 'itemsPerPage', parseInt(e.target.value))}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      >
                        {[12, 24, 36, 48, 60].map((number) => (
                          <option key={number} value={number}>
                            {number} items
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Default Sort Order
                      </label>
                      <select
                        value={settings.display.defaultSortOrder}
                        onChange={(e) => handleSettingChange('display', 'defaultSortOrder', e.target.value)}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      >
                        <option value="newest">Newest First</option>
                        <option value="oldest">Oldest First</option>
                        <option value="price_low">Price: Low to High</option>
                        <option value="price_high">Price: High to Low</option>
                        <option value="popular">Most Popular</option>
                      </select>
                    </div>
                  </div>

                  <div className="space-y-4">
                    {[
                      {
                        key: 'showViews' as keyof typeof settings.display,  
                        label: 'Show View Count',
                        description: 'Display number of views on listings'
                      },
                      {
                        key: 'showBookmarks',
                        label: 'Show Bookmark Count',
                        description: 'Display number of bookmarks on listings'
                      },
                      {
                        key: 'showShareButtons',
                        label: 'Show Share Buttons',
                        description: 'Display social sharing buttons'
                      },
                      {
                        key: 'showContactInfo',
                        label: 'Show Contact Information',
                        description: 'Display contact information on listings'
                      }
                    ].map(({ key, label, description }) => (
                      <div key={key} className="flex items-center justify-between">
                        <div>
                          <h3 className="text-sm font-medium text-gray-700">{label}</h3>
                          <p className="text-sm text-gray-500">{description}</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={Boolean(settings.display[key as keyof typeof settings.display])}
                            onChange={(e) => handleSettingChange('display', key, e.target.checked)}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Pricing Settings */}
              {activeTab === 'pricing' && (
                <div className="space-y-6">
                  <h2 className="text-lg font-medium text-gray-900 mb-6">Pricing Settings</h2>
                  
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Minimum Price
                      </label>
                      <div className="mt-1 relative rounded-md shadow-sm">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <span className="text-gray-500 sm:text-sm">$</span>
                        </div>
                        <input
                          type="number"
                          value={settings.pricing.minPrice}
                          onChange={(e) => handleSettingChange('pricing', 'minPrice', parseFloat(e.target.value))}
                          className="block w-full pl-7 rounded-md border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Maximum Price
                      </label>
                      <div className="mt-1 relative rounded-md shadow-sm">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <span className="text-gray-500 sm:text-sm">$</span>
                        </div>
                        <input
                          type="number"
                          value={settings.pricing.maxPrice}
                          onChange={(e) => handleSettingChange('pricing', 'maxPrice', parseFloat(e.target.value))}
                          className="block w-full pl-7 rounded-md border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Default Currency
                      </label>
                      <select
                        value={settings.pricing.defaultCurrency}
                        onChange={(e) => handleSettingChange('pricing', 'defaultCurrency', e.target.value)}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      >
                        {settings.pricing.supportedCurrencies.map((currency) => (
                          <option key={currency} value={currency}>{currency}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-sm font-medium text-gray-700">Allow Price Range</h3>
                        <p className="text-sm text-gray-500">
                          Allow users to specify a price range instead of a fixed price
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={settings.pricing.allowPriceRange}
                          onChange={(e) => handleSettingChange('pricing', 'allowPriceRange', e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-sm font-medium text-gray-700">Require Price</h3>
                        <p className="text-sm text-gray-500">
                          Make price a required field for all listings
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={settings.pricing.requirePrice}
                          onChange={(e) => handleSettingChange('pricing', 'requirePrice', e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {/* Security Settings */}
              {activeTab === 'security' && (
                <div className="space-y-6">
                  <h2 className="text-lg font-medium text-gray-900 mb-6">Security Settings</h2>
                  
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Maximum Reports Before Review
                      </label>
                      <input
                        type="number"
                        value={settings.security.maxReportsBeforeReview}
                        onChange={(e) => handleSettingChange('security', 'maxReportsBeforeReview', parseInt(e.target.value))}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        />
                        <p className="mt-1 text-sm text-gray-500">
                        Number of reports before listing is flagged for review
                      </p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-sm font-medium text-gray-700">Require Email Verification</h3>
                        <p className="text-sm text-gray-500">
                          Require users to verify their email before posting listings
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={settings.security.requireEmailVerification}
                          onChange={(e) => handleSettingChange('security', 'requireEmailVerification', e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-sm font-medium text-gray-700">Require Phone Verification</h3>
                        <p className="text-sm text-gray-500">
                          Require users to verify their phone number before posting
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={settings.security.requirePhoneVerification}
                          onChange={(e) => handleSettingChange('security', 'requirePhoneVerification', e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-sm font-medium text-gray-700">Spam Protection</h3>
                        <p className="text-sm text-gray-500">
                          Enable automatic spam detection and prevention
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={settings.security.spamProtection}
                          onChange={(e) => handleSettingChange('security', 'spamProtection', e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                  </div>

                  <div className="mt-6">
                    <label className="block text-sm font-medium text-gray-700">
                      Blocked Keywords
                    </label>
                    <div className="mt-2">
                      <textarea
                        value={settings.security.blockedKeywords.join('\n')}
                        onChange={(e) => handleSettingChange(
                          'security',
                          'blockedKeywords',
                          e.target.value.split('\n').filter(Boolean)
                        )}
                        rows={4}
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        placeholder="Enter one keyword per line"
                      />
                      <p className="mt-1 text-sm text-gray-500">
                        Keywords that will trigger listing review or rejection. Enter one per line.
                      </p>
                    </div>
                  </div>

                  {/* Additional Security Section */}
                  <div className="mt-8 p-4 bg-gray-50 rounded-lg">
                    <h3 className="text-sm font-medium text-gray-900 mb-4">Advanced Security Settings</h3>
                    <div className="space-y-4">
                      {/* Content Scanning */}
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="text-sm font-medium text-gray-700">Content Scanning</h4>
                          <p className="text-sm text-gray-500">
                            Scan listing content for inappropriate material
                          </p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={settings.security.contentScanning}
                            onChange={(e) => handleSettingChange('security', 'contentScanning', e.target.checked)}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                        </label>
                      </div>

                      {/* IP Tracking */}
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="text-sm font-medium text-gray-700">IP Tracking</h4>
                          <p className="text-sm text-gray-500">
                            Track and monitor IP addresses for suspicious activity
                          </p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={settings.security.ipTracking}
                            onChange={(e) => handleSettingChange('security', 'ipTracking', e.target.checked)}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                        </label>
                      </div>

                      {/* Rate Limiting */}
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Rate Limiting</h4>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs text-gray-500">Maximum Listings per Hour</label>
                            <input
                              type="number"
                              value={settings.security.maxListingsPerHour}
                              onChange={(e) => handleSettingChange('security', 'maxListingsPerHour', parseInt(e.target.value))}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500">Maximum Listings per Day</label>
                            <input
                              type="number"
                              value={settings.security.maxListingsPerDay}
                              onChange={(e) => handleSettingChange('security', 'maxListingsPerDay', parseInt(e.target.value))}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Security Log Settings */}
                  <div className="mt-6">
                    <h3 className="text-sm font-medium text-gray-900 mb-4">Security Logging</h3>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="text-sm font-medium text-gray-700">Enable Security Logs</h4>
                          <p className="text-sm text-gray-500">
                            Keep detailed logs of security-related events
                          </p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={settings.security.enableSecurityLogs}
                            onChange={(e) => handleSettingChange('security', 'enableSecurityLogs', e.target.checked)}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                        </label>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">Log Retention Period</label>
                        <select
                          value={settings.security.logRetentionDays}
                          onChange={(e) => handleSettingChange('security', 'logRetentionDays', parseInt(e.target.value))}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        >
                          <option value="30">30 days</option>
                          <option value="60">60 days</option>
                          <option value="90">90 days</option>
                          <option value="180">180 days</option>
                          <option value="365">1 year</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Save Changes Feedback */}
      {hasChanges && (
        <div className="fixed bottom-0 inset-x-0 pb-6">
          <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-blue-600 rounded-lg shadow-lg p-4">
              <div className="flex items-center justify-between flex-wrap">
                <div className="w-0 flex-1 flex items-center">
                  <span className="flex p-2 rounded-lg bg-blue-800">
                    <AlertCircle className="h-6 w-6 text-white" />
                  </span>
                  <p className="ml-3 font-medium text-white truncate">
                    <span>You have unsaved changes</span>
                  </p>
                </div>
                <div className="flex-shrink-0 sm:ml-3">
                  <button
                    type="button"
                    onClick={handleSave}
                    className="flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-blue-600 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-blue-600 focus:ring-white"
                  >
                    Save Changes
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ListingSystemSettings;